import React from "react";
import { Inbox } from "lucide-react";

import JobTabs from "./JobTabs";
import JobCard from "./JobCard";
import JobDetailsPanel from "./JobDetailsPanel";

const ACTIVE_STATUSES = new Set(["queued", "running"]);

export default function WorkspaceSection({
  jobs = [],
  jobsLoading,
  jobsError,
  activeTab,
  setActiveTab,
  selectedJob,
  setSelectedJob,
  clips = [],
  clipsLoading,
  handleSelectJob,
  handleRetry,
  handleRefreshResults,
  renderTimeline,
  getJobStateUI,
  setPreviewClip,
  onAdjustClip,
  retrimState,
}) {
  const analysisJobs = jobs.filter(
    (job) => !job.job_type || job.job_type === "analysis",
  );

  const queuedJobs = analysisJobs.filter((job) =>
    ACTIVE_STATUSES.has(job.status),
  );

  const completedJobs = analysisJobs.filter(
    (job) => job.status === "completed",
  );

  const failedJobs = analysisJobs.filter((job) => job.status === "failed");

  const jobsByTab = {
    queue: queuedJobs,
    completed: completedJobs,
    failed: failedJobs,
  };

  const jobsToShow = jobsByTab[activeTab] || [];

  const changeTab = (tab) => {
    setActiveTab(tab);

    const nextJobs = jobsByTab[tab] || [];

    const selectedStillVisible = nextJobs.some(
      (job) => job.id === selectedJob?.id,
    );

    if (selectedStillVisible) {
      return;
    }

    if (nextJobs.length > 0) {
      handleSelectJob(nextJobs[0]);

      return;
    }

    setSelectedJob(null);
  };

  return (
    <section className="grid items-start gap-5 lg:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[300px_minmax(0,1fr)]">
      {/* Job list */}

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <JobTabs
          activeTab={activeTab}
          setActiveTab={changeTab}
          setSelectedJob={setSelectedJob}
          counts={{
            queue: queuedJobs.length,

            completed: completedJobs.length,

            failed: failedJobs.length,
          }}
        />

        {/* Status error */}

        {jobsError && (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          >
            {jobsError}
          </div>
        )}

        {/* Loading jobs */}

        {jobsLoading && jobs.length === 0 ? (
          <div className="space-y-3" aria-label="Loading jobs">
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="h-24 animate-pulse rounded-xl bg-gray-100"
              />
            ))}
          </div>
        ) : jobsToShow.length > 0 ? (
          /* Job cards */

          <div className="space-y-3">
            {jobsToShow.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                isActive={selectedJob?.id === job.id}
                onClick={handleSelectJob}
              />
            ))}
          </div>
        ) : (
          /* Empty state */

          <div className="flex min-h-52 flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center">
            <Inbox className="h-8 w-8 text-gray-300" aria-hidden="true" />

            <p className="mt-3 text-sm font-medium text-gray-700">
              No {activeTab === "queue" ? "active" : activeTab} analyses
            </p>

            <p className="mt-1 max-w-xs text-xs text-gray-500">
              {activeTab === "queue"
                ? "Upload a source video and start an analysis to see it here."
                : `Your ${activeTab} analyses will appear here.`}
            </p>
          </div>
        )}
      </div>

      {/* Selected job details */}

      <div className="min-h-[420px] min-w-0 overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
        <JobDetailsPanel
          selectedJob={selectedJob}
          clips={clips}
          clipsLoading={clipsLoading}
          handleRetry={handleRetry}
          handleRefreshResults={handleRefreshResults}
          renderTimeline={renderTimeline}
          getJobStateUI={getJobStateUI}
          setPreviewClip={setPreviewClip}
          onAdjustClip={onAdjustClip}
          retrimState={retrimState}
        />
      </div>
    </section>
  );
}
